'use strict';

/**
 * Pict-Provider-NotebookAPI
 * -------------------------
 * Browser-side client for the per-user notebook: markdown notes, bookmarks
 * (with favorites + history), and the caller's personal checklists. Everything
 * is private to the signed-in user (the server scopes Note/Bookmark/ViewEvent by
 * IDUser). The side panel loads the whole thing in one shot via GET /1.0/Notebook;
 * notes and bookmarks are plain auto-CRUD (private via the personal-scope hook).
 *
 * AppData shape this provider owns:
 *   AppData.Notebook = {
 *       Open: false,            // panel visibility
 *       ActiveTab: 'notes',     // 'notes' | 'bookmarks' | 'checklists'
 *       Loaded: false,
 *       LoadError: null,
 *       Notes: [], Bookmarks: [], Favorites: [], Recent: [], Trending: [], Checklists: []
 *   }
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'NotebookAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderNotebookAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.Notebook)
		{
			this.fable.AppData.Notebook =
			{
				Open: false,
				ActiveTab: 'notes',
				Loaded: false,
				LoadError: null,
				Notes: [],
				Bookmarks: [],
				Favorites: [],
				Recent: [],
				Trending: [],
				Checklists: [],
				Gallery: []
			};
		}
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	_json(pMethod, pPath, pBody)
	{
		let tmpURL = this._url(pPath);
		switch (pMethod)
		{
			case 'POST':
				return libFrameworkClient.postJSON(this.fable, tmpURL, pBody !== undefined ? pBody : {});
			case 'PUT':
				return libFrameworkClient.putJSON(this.fable, tmpURL, pBody !== undefined ? pBody : {});
			case 'DELETE':
				return libFrameworkClient.delJSON(this.fable, tmpURL);
			default:
				return libFrameworkClient.getJSON(this.fable, tmpURL);
		}
	}

	// One read backs the panel.
	loadNotebook()
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.Notebook;
		return this._json('GET', 'Notebook')
			.then((pBody) =>
			{
				tmpStore.Notes = pBody.Notes || [];
				tmpStore.Bookmarks = pBody.Bookmarks || [];
				tmpStore.Favorites = pBody.Favorites || [];
				tmpStore.Recent = pBody.Recent || [];
				tmpStore.Trending = pBody.Trending || [];
				tmpStore.Checklists = pBody.Checklists || [];
				tmpStore.Loaded = true;
				tmpStore.LoadError = null;
				return pBody;
			})
			.catch((pErr) => { tmpStore.LoadError = pErr.message; return null; });
	}

	// Notes -- private auto-CRUD.
	createNote(pNote) { return this._json('POST', 'Note', pNote || { Title: 'Untitled note', Content: '' }); }
	updateNote(pIDNote, pPatch) { return this._json('PUT', 'Note', Object.assign({ IDNote: pIDNote }, pPatch || {})); }
	deleteNote(pIDNote) { return this._json('DELETE', 'Note/' + pIDNote); }

	// Bookmarks -- private auto-CRUD, plus the favorites reorder convenience route.
	createBookmark(pBookmark) { return this._json('POST', 'Bookmark', pBookmark || {}); }
	updateBookmark(pIDBookmark, pPatch) { return this._json('PUT', 'Bookmark', Object.assign({ IDBookmark: pIDBookmark }, pPatch || {})); }
	deleteBookmark(pIDBookmark) { return this._json('DELETE', 'Bookmark/' + pIDBookmark); }
	setFavorite(pIDBookmark, pFavorite) { return this.updateBookmark(pIDBookmark, { Favorite: pFavorite ? 1 : 0 }); }
	reorderFavorites(pOrder) { return this._json('POST', 'Notebook/Favorites/Reorder', { Order: pOrder || [] }); }

	// History -- one row per navigation. A page's specific title is often not known at
	// navigation time: detail pages refine document.title only after their record loads
	// (see PictView-EntityDetail), so the same route is recorded twice for one visit --
	// first with a generic title ("Plansheet"), then with the record's own name. Collapse
	// that into a single row: the first record for a route CREATEs the ViewEvent and
	// remembers its id; a later record for the SAME route UPDATEs that row's label instead
	// of appending a duplicate. Fire-and-forget: a failed record must never disrupt
	// navigation, so every path swallows its errors.
	recordView(pView)
	{
		try
		{
			let tmpView = pView || {};
			let tmpRoute = tmpView.Route || '';
			let tmpLabel = tmpView.Label || '';
			if (this._lastView && this._lastView.Route === tmpRoute)
			{
				// Same visit -- relabel the in-flight / created row rather than adding another.
				if (!tmpLabel || tmpLabel === this._lastView.Label) { return; }
				this._lastView.Label = tmpLabel;
				let tmpTracker = this._lastView;
				tmpTracker.Ready = tmpTracker.Ready.then((pID) =>
					((pID && tmpTracker === this._lastView)
						? this._json('PUT', 'ViewEvent', { IDViewEvent: pID, Label: tmpLabel }).then(() => pID, () => pID)
						: pID));
				return;
			}
			let tmpTracker = { Route: tmpRoute, Label: tmpLabel };
			tmpTracker.Ready = this._json('POST', 'ViewEvent', tmpView)
				.then((pResult) => { let tmpRow = Array.isArray(pResult) ? pResult[0] : pResult; return (tmpRow && tmpRow.IDViewEvent) || 0; }, () => 0);
			this._lastView = tmpTracker;
		}
		catch (pError) { /* never throw from a navigation side effect */ }
	}
}

module.exports = PictProviderNotebookAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
